import classNames from "classnames/bind";
import React, { useState } from "react";
import Button from "../Button/Button";
import { IconLoading } from "../Icon";
import { Icon } from "../Icon/Icon";
import styles from "./Banner.module.scss";
import type { BannerProps } from "./types";

const cx = classNames.bind(styles);

/**
 * A component that shows some information with the intent of an aside or providing context to existing information
 */
const Banner = ({
  titleText,
  description,
  icon,
  actionContent,
  isDismissable = false,
  color,
  variant = "generic",
  className,
  onDismiss,
  onHover,
  style,
}: BannerProps) => {
  const [isDismissed, setIsDismissed] = useState(false);

  const handleHover = () => {
    if (onHover) {
      onHover();
    }
  };

  const handleDismissClick = () => {
    setIsDismissed((prevState) => !prevState);

    if (onDismiss) {
      onDismiss(setIsDismissed);
    }
  };

  const getPresetIcon = () => {
    switch (variant) {
      case "success":
        return (
          <Icon
            variant="check-circle"
            size={24}
          />
        );
      case "error":
        return (
          <Icon
            variant="warning-circle"
            size={24}
          />
        );
      case "info":
        return (
          <Icon
            variant="info"
            size={24}
          />
        );
      case "loading":
        return (
          <IconLoading
            speed="slow"
            size={24}
          />
        );
      case "warning":
        return (
          <Icon
            variant="warning"
            size={24}
          />
        );
      default:
        return null;
    }
  };

  const renderedIcon = icon ?? getPresetIcon();

  return (
    <aside
      className={cx(
        "container",
        variant && !color && `container--${variant}`,
        color && `container--${color}`,
        isDismissed && "container--hidden",
        className
      )}
      aria-hidden={isDismissed}
      onMouseEnter={handleHover}
      style={style}
    >
      <div className={cx("primaryContent")}>
        {renderedIcon ? (
          <div
            className={cx(
              "icon",
              (color ?? variant) && `icon--${color ?? variant}`
            )}
          >
            {renderedIcon}
          </div>
        ) : null}
        <p
          className={cx("title", !renderedIcon && "no-icon")}
          aria-label="Banner title"
        >
          {titleText}
        </p>
        {actionContent ? (
          <div className={cx("action")}>{actionContent}</div>
        ) : null}
        {isDismissable ? (
          <Button
            icon="x-circle"
            iconProps={{ size: 24 }}
            variant="text"
            style={{ padding: 0 }}
            className={cx("dismiss", actionContent && "dismiss--with-action")}
            onClick={handleDismissClick}
            aria-label="Dismiss this banner"
          />
        ) : null}
      </div>
      <div
        className={cx(
          "secondaryContent",
          !description && "secondaryContent--hidden"
        )}
      >
        {typeof description === "string" ? (
          <p className={cx("description", !renderedIcon && "no-icon")}>
            {description}
          </p>
        ) : (
          <div className={cx("description", !renderedIcon && "no-icon")}>
            {description}
          </div>
        )}
      </div>
    </aside>
  );
};

export default Banner;
